import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { APP_CONSTANTS as CONST } from '@app/app.constants';
import { Observable, Subject, timer } from 'rxjs';
import { scan, takeUntil, takeWhile } from 'rxjs/operators';
import { ISkeleton } from '../../../../core/models/skeleton.model';

@Component({
  selector: 'form-otp-code',
  templateUrl: './input-otp-code.component.html',
  styleUrls: ['./input-otp-code.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputOtpCodeComponent),
      multi: true,
    },
  ],
})
export class InputOtpCodeComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() label: string;
  @Input() length: number;
  @Input() seconds: number;
  @Input() isLoading: boolean;
  @Input() error: boolean = false;
  @Output() resendCodeEvent = new EventEmitter<boolean>();
  @Output() loginEvent = new EventEmitter<string>();
  @Output() errorGetNewCodeEvent = new EventEmitter<boolean>();
  @Output() errorStillNotReceivingCodeEvent = new EventEmitter<boolean>();
  @Output() modalWhereIsTheDateEvent = new EventEmitter<boolean>();
  @Output() currentTime = new EventEmitter<number>();

  public value?: string = '';
  public values: Array<number> = [];
  public code?: number;
  public inputs?: Array<number>;
  public isDisabled?: boolean;
  public regexNumber: RegExp = /[0-9]/;
  public skeletonInfo: ISkeleton;
  public isLargeScreen: boolean;

  // Timer Config
  public startTimer$: Observable<number>;
  public time: number = 0;
  public currentTimer: number;
  public stopTimer$: Subject<boolean> = new Subject();

  private _onChange = (_: any) => {};
  private _onTouch = () => {};

  constructor() {}
  @HostListener('window:resize', ['$event'])
  checkIsLarge() {
    this._setViewLarge();
  }

  ngOnInit(): void {
    this._setValues();
  }

  ngAfterViewInit(): void {
    this._autoFocus();
    this._setSkeleton();
  }

  private _setValues() {
    this.length = 4;
    this.values = Array(this.length).fill('');
    this.inputs = Array(this.length);
    this.isLargeScreen = window.innerWidth > CONST.SIZE_MOBILE_SCREEN;
    this.startTimer$ = timer(0, 1000).pipe(
      scan((acc) => --acc, this.seconds),
      takeWhile((time) => time >= 0),
      takeUntil(this.stopTimer$)
    );
    this._onStartTimer();
  }

  private _setSkeleton() {
    const box = document.querySelector('.input-otp-code__timer');
    const positionInfo = box?.getBoundingClientRect();
    const width = positionInfo?.width - 30;
    this.skeletonInfo = this.isLargeScreen
      ? {
          appearance: 'line',
          theme: {
            height: '95px',
            width: '75px',
          },
        }
      : {
          appearance: 'line',
          theme: {
            height: '95px',
            width: `${width / 4}px`,
          },
        };
  }

  private _createCode(values: number[]) {
    return values.reduce((code?: string, number?: number) => `${code}${number}` ?? '', '');
  }

  private _validateRegex(value) {
    return this.regexNumber.test(value);
  }

  private _onStartTimer() {
    this.startTimer$.subscribe((timer) => {
      this.time = timer;
      this.currentTimer = timer;
    });
  }

  private _onFinishTimer() {
    this.stopTimer$.next(true);
    this.time = 0;
  }

  private _onSendCode(value: string) {
    if (value.length === 4) {
      this.loginEvent.emit(value);
      this.currentTime.emit(this.currentTimer);
    }
  }

  private _autoFocus() {
    const selector = `field-0`;
    document.getElementById(selector)?.focus();
  }

  private _setViewLarge(): void {
    this.isLargeScreen = window.innerWidth > CONST.SIZE_MOBILE_SCREEN;
  }

  public onChangeValue() {
    this._onChange(this.code);
  }

  public writeValue(value: string): void {
    const valueNumber = Number(value);

    if (!value) this.values?.fill(NaN);

    if (valueNumber && value.length <= this.length && this.values) {
      for (let i = 0; i < value.length; i++) {
        this.values[i] = Number(value.charAt(i));
      }
    }
  }

  public registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this._onTouch = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.isLoading = changes['isLoading']?.currentValue ?? false;
    this.seconds = changes['seconds']?.currentValue ?? false;
  }

  public onInput(event: InputEvent, index: number) {
    const value = event.target['value'];

    if (!this.values) return;

    if (!this._validateRegex(value)) {
      event.target['value'] = '';
      this.values[index] = null;
      return;
    }

    const inputValue = parseInt(value, 10);

    if (isNaN(inputValue) || inputValue < 0 || inputValue > 9) {
      event.target['value'] = '';
      this.values[index] = null;
      return;
    }

    this.values[index] = inputValue;
    this.value = this._createCode(this.values.filter((val) => val !== null));

    this._onSendCode(this.value);

    this._onTouch();

    if (value.length >= 1 && value != '') {
      const nextIndex = index + 1;
      if (nextIndex && nextIndex < this.length) {
        const selector = `field-${nextIndex}`;
        document.getElementById(selector).focus();
      }
    }
  }

  public onKeydown(event: KeyboardEvent, index: number) {
    const isDeleteKey = event.key === 'Backspace';
    const isArrowUp = event.key === 'ArrowUp';
    const isArrowDown = event.key === 'ArrowDown';
    const value = event.target['value'];

    this.values[index] = value;
    this.value = this._createCode(this.values);

    this._onTouch();
    this._onChange(this.value);

    if (isDeleteKey && !value) {
      const prevIndex = index - 1;
      if (prevIndex >= 0) {
        const selector = `field-${prevIndex}`;
        document.getElementById(selector).focus();
      }
    }

    if (isArrowUp || isArrowDown) {
      event.preventDefault();
    }
  }

  public onResendCode() {
    this.resendCodeEvent.emit(true);
    this._onStartTimer();
  }

  public onOpenErrorGetNewCode() {
    this.errorGetNewCodeEvent.emit(true);
  }

  public onOpenErrorStillNotReceivingCode() {
    this.errorStillNotReceivingCodeEvent.emit(true);
  }
}
